import { useState, useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { resolvePricingRate } from '@/lib/api/pricing'
import { useLocationCatalog } from '@/hooks/usePricing'
import {
  SERVICE_TYPE_LABELS,
  UNIT_OF_MEASURE_LABELS,
  PRICING_SOURCE_LABELS,
  type ResolvedCatalogItem,
  type PricingResolutionResult,
  type PricingSource,
  type ServiceType,
} from '@/types/pricing'
import { pricingKeys } from '@/hooks/usePricing'

const SOURCE_COLORS: Record<PricingSource, string> = {
  policy: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  contract: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  manual: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  override: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  dynamic: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
}

interface RateSheetPreviewProps {
  locationId: string
}

export function RateSheetPreview({ locationId }: RateSheetPreviewProps) {
  const [serviceDate, setServiceDate] = useState(
    new Date().toISOString().split('T')[0]
  )
  const { data: catalog, isLoading: catalogLoading } = useLocationCatalog(locationId)

  // Limit to first 20 items to avoid excessive RPCs
  const activeItems = useMemo(() => (catalog ?? []).slice(0, 20), [catalog])

  // Resolve rates for all active items
  const rateQueries = useQueries({
    queries: activeItems.map((item) => ({
      queryKey: pricingKeys.resolution({
        locationId,
        catalogItemCode: item.code,
        serviceDate,
      }),
      queryFn: () =>
        resolvePricingRate({
          locationId,
          catalogItemCode: item.code,
          serviceDate,
        }),
      enabled: !!locationId && activeItems.length > 0,
      staleTime: 30_000,
    })),
  })

  type CatalogEntry = { item: ResolvedCatalogItem; result: PricingResolutionResult | undefined; isLoading: boolean }

  // Group items by service type, then sub-group by groupName
  const grouped = useMemo(() => {
    const typeGroups = new Map<ServiceType, CatalogEntry[]>()

    activeItems.forEach((item, i) => {
      const query = rateQueries[i]
      const entry: CatalogEntry = {
        item,
        result: query?.data as PricingResolutionResult | undefined,
        isLoading: query?.isLoading ?? true,
      }
      const list = typeGroups.get(item.type) ?? []
      list.push(entry)
      typeGroups.set(item.type, list)
    })

    return typeGroups
  }, [activeItems, rateQueries])

  // Build sub-groups within a type group
  const getSubGroups = (entries: CatalogEntry[]) => {
    const subMap = new Map<string, CatalogEntry[]>()
    for (const entry of entries) {
      const key = entry.item.groupName ?? 'Other'
      const list = subMap.get(key) ?? []
      list.push(entry)
      subMap.set(key, list)
    }
    return Array.from(subMap.entries()).sort(([, a], [, b]) =>
      (a[0]?.item.groupSortOrder ?? 999) - (b[0]?.item.groupSortOrder ?? 999)
    )
  }

  if (catalogLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    )
  }

  if (!catalog || catalog.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        No services configured for this location.
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Label htmlFor="rate-date" className="text-sm whitespace-nowrap">
          Rates for:
        </Label>
        <Input
          id="rate-date"
          type="date"
          value={serviceDate}
          onChange={(e) => setServiceDate(e.target.value)}
          className="w-44"
        />
      </div>

      <div className="space-y-4">
        {Array.from(grouped.entries()).map(([type, entries]) => {
          const subGroups = getSubGroups(entries)
          const hasMultipleSubGroups = subGroups.length > 1

          return (
            <div key={type}>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">
                {SERVICE_TYPE_LABELS[type] ?? type}
              </h4>
              <div className="rounded-md border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-3 py-2 font-medium">Service</th>
                      <th className="text-right px-3 py-2 font-medium">Rate</th>
                      <th className="text-left px-3 py-2 font-medium">Unit</th>
                      <th className="text-left px-3 py-2 font-medium">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subGroups.map(([groupName, groupEntries]) => (
                      <>
                        {hasMultipleSubGroups && (
                          <tr key={`header-${groupName}`} className="bg-muted/30">
                            <td colSpan={4} className="px-3 py-1.5 text-xs font-medium text-muted-foreground">
                              {groupName}
                            </td>
                          </tr>
                        )}
                        {groupEntries.map(({ item, result, isLoading }) => (
                          <tr key={item.catalogItemId} className="border-b last:border-0">
                            <td className="px-3 py-2">{item.displayName}</td>
                            <td className="px-3 py-2 text-right font-mono">
                              {isLoading ? (
                                <Skeleton className="h-4 w-16 ml-auto" />
                              ) : result?.unit_price != null ? (
                                `$${Number(result.unit_price).toFixed(2)}`
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">
                              {UNIT_OF_MEASURE_LABELS[item.unitOfMeasure as keyof typeof UNIT_OF_MEASURE_LABELS] ?? item.unitOfMeasure}
                            </td>
                            <td className="px-3 py-2">
                              {isLoading ? (
                                <Skeleton className="h-5 w-20" />
                              ) : result?.source ? (
                                <div className="flex items-center gap-1.5">
                                  <Badge
                                    className={`text-[10px] px-1.5 py-0 h-5 ${SOURCE_COLORS[result.source] ?? ''}`}
                                    variant="secondary"
                                  >
                                    {PRICING_SOURCE_LABELS[result.source]}
                                  </Badge>
                                  {result.source_detail?.policy_name && (
                                    <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                                      {result.source_detail.policy_name}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-xs">No rate</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
